<script setup lang="ts">
import type { THeading } from '~/components/ui/heading'

const props = defineProps<{
  headingLevel?: THeading
  leadingLevel?: string
  variables?: Record<string, string | number>
}>()

const route = useRoute()

const nuxtApp = useNuxtApp()
const existingSeoData = computed(() => nuxtApp._seoData?.value)

const { data: cachedData } = existingSeoData.value
  ? { data: ref({ data: { data: {} } }) }
  : await useAppCache()

const seoData = computed(() => {
  if (existingSeoData.value) {
    return existingSeoData.value
  }

  let currentPath = route.path.startsWith('/') ? route.path.substring(1) : route.path

  if (currentPath.startsWith('ky/')) {
    currentPath = currentPath.substring(3)
  }

  const seoEntries = cachedData.value?.data?.data || {}

  if (seoEntries[currentPath]) return seoEntries[currentPath]

  for (const key in seoEntries) {
    if (key.includes('(')) {
      const regex = new RegExp(`^${key}$`)
      if (regex.test(currentPath)) {
        return seoEntries[key]
      }
    }
  }

  return null
})

const replaceVariables = (text: string | null) => {
  if (!text) return ''

  return text.replace(/:([a-zA-Z0-9_.]+):/g, (_, key) => {
    return props.variables?.[key] !== undefined ? String(props.variables[key]) : ''
  })
}

const title = computed(() => {
  return seoData.value ? replaceVariables(seoData.value.title) : ''
})

const content = computed(() => {
  if (!seoData.value) {
    return ''
  }
  return replaceVariables(seoData.value.content)
})

const computedLeading = computed(() => {
  return props.leadingLevel ?? 10
})
</script>

<template>
  <div v-if="title || content">
    <ContentBlock variant="white">
      <Heading
        v-if="title"
        :tag="headingLevel ?? 'h1'"
        :class="`leading-${computedLeading}`"
      >
        {{ title }}
      </Heading>
      <Content v-if="content && content.length > 0">
        <div class="mx-auto max-w-none space-y-5" v-html="content" />
      </Content>
      <slot />
    </ContentBlock>
  </div>
</template>
